import { apiClient } from './client';

export interface Procedure {
  id: string;
  /** Per-tenant human-friendly number, assigned by the server. */
  procedure_number: number;
  name: string;
  /** Integer 0..100. */
  threshold_percent: number;
  /**
   * Optional group label for procedures that belong to the same
   * exercise block. Null for one-off / ad-hoc procedures.
   */
  session_name: string | null;
  /** ISO date (YYYY-MM-DD) or null. */
  day: string | null;
  /**
   * Optional mapping to a `public.master_baselines.procedure_group`
   * value (ROADMAP #44). When set + matching a master row, the
   * participant grid renders the faculty / experienced-cohort
   * comparison bars for this procedure. Null = no master mapping.
   */
  procedure_group: string | null;
  /**
   * How the procedure is performed: 'manual' | 'robotic' (later a specific
   * robotic platform). Sessions inherit it, and the scorecard only compares
   * against same-operator master baselines. Defaults to 'manual'.
   */
  operator: string;
  created_at: string;
  updated_at: string;
}

export type ProcedureOperator = 'manual' | 'robotic';

export const PROCEDURE_OPERATORS: {
  value: ProcedureOperator;
  label: string;
}[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'robotic', label: 'Robotic' },
];

export interface ProcedureCreate {
  name: string;
  threshold_percent: number;
  /** Optional. Null or empty string both mean "no session". */
  session_name?: string | null;
  day?: string | null;
  /** Master baseline group key — pick from /master-baselines/groups. */
  procedure_group?: string | null;
  /** 'manual' | 'robotic'. Omit to default to 'manual'. */
  operator?: string;
}

export interface ProcedureUpdate {
  name?: string;
  threshold_percent?: number;
  /** Send null to clear the session label; omit to leave unchanged. */
  session_name?: string | null;
  /** Send null to explicitly clear the date; omit to leave unchanged. */
  day?: string | null;
  /** Send empty string or null to clear; omit to leave unchanged. */
  procedure_group?: string | null;
  /** 'manual' | 'robotic'. Omit to leave unchanged. */
  operator?: string;
}

export async function listProcedures(
  tenantId: string,
): Promise<Procedure[]> {
  const { data } = await apiClient.get<Procedure[]>(
    `/api/v1/tenants/${tenantId}/procedures`,
  );
  return data;
}

export async function createProcedure(
  tenantId: string,
  body: ProcedureCreate,
): Promise<Procedure> {
  const { data } = await apiClient.post<Procedure>(
    `/api/v1/tenants/${tenantId}/procedures`,
    body,
  );
  return data;
}

export async function updateProcedure(
  tenantId: string,
  procedureId: string,
  body: ProcedureUpdate,
): Promise<Procedure> {
  const { data } = await apiClient.patch<Procedure>(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}`,
    body,
  );
  return data;
}

export async function deleteProcedure(
  tenantId: string,
  procedureId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}`,
  );
}
